import React, { useState, useEffect } from 'react';
import api, { getPublicRegistrationSettings } from '../utils/api';
import { ar, en } from '../utils/translations';
import {
  validateRegistrationName,
  validateRegistrationBusiness,
  validateRegistrationEmail,
  validateRegistrationPhoneLocal,
} from '../utils/registrationValidation';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './Auth.css';
import realLogo from '../assets/real-logo.png';

const Auth = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [language, setLanguage] = useState(() => {
    // Get saved language preference or default to Arabic
    return localStorage.getItem('language') || 'ar';
  });
  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    email: '',
    password: ''
  });
  const [phoneValue, setPhoneValue] = useState('966');
  const [phoneDialCode, setPhoneDialCode] = useState('966');
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');
  const [businessError, setBusinessError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorTimestamp, setErrorTimestamp] = useState(null);
  /** Shown after register when verification email is sent */
  const [emailVerificationPending, setEmailVerificationPending] = useState(null);
  /** Public flags from server: close self-service sign-up during rollout */
  const [publicRegSettings, setPublicRegSettings] = useState(null);
  const t = language === 'ar' ? ar : en;
  const isRTL = language === 'ar';

  useEffect(() => {
    let cancelled = false;
    getPublicRegistrationSettings()
      .then((res) => {
        if (!cancelled) setPublicRegSettings(res.data);
      })
      .catch(() => {
        if (!cancelled) setPublicRegSettings({ public_signup_enabled: true });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const validatePhoneNumber = (phone, countryCode) =>
    validateRegistrationPhoneLocal(phone, countryCode, language);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === 'name') setNameError(validateRegistrationName(value, language));
    if (name === 'business_name') setBusinessError(validateRegistrationBusiness(value, language));
    if (name === 'email') setEmailError(validateRegistrationEmail(value, language));
    
    // Only clear general error if it's been shown for at least 3 seconds
    if (errorTimestamp && Date.now() - errorTimestamp > 3000) {
      setError('');
      setErrorTimestamp(null);
    }
  };

  const handlePhoneChange = (value, data) => {
    setPhoneValue(value);
    setPhoneDialCode(data.dialCode);
    const localNumber = value.slice(data.dialCode.length);
    const phoneValidationError = validatePhoneNumber(localNumber, '+' + data.dialCode);
    setPhoneError(phoneValidationError);
    if (errorTimestamp && Date.now() - errorTimestamp > 3000) {
      setError('');
      setErrorTimestamp(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorTimestamp(null);
    setLoading(true);

    // Validate phone number before submission
    const localNumber = phoneValue.slice(phoneDialCode.length);
    const phoneValidationError = validatePhoneNumber(localNumber, '+' + phoneDialCode);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      setError(phoneValidationError);
      setLoading(false);
      return;
    }

    const fullPhoneNumber = '+' + phoneValue;

    try {
      if (isLogin) {
        // Login
        const response = await api.post('/auth/login', {
          phone_number: fullPhoneNumber,
          password: formData.password
        });
        
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          onLogin(response.data.user);
        }
      } else {
        const nextNameError = validateRegistrationName(formData.name, language);
        const nextBusinessError = validateRegistrationBusiness(formData.business_name, language);
        const nextEmailError = validateRegistrationEmail(formData.email, language);
        setNameError(nextNameError);
        setBusinessError(nextBusinessError);
        setEmailError(nextEmailError);

        if (nextNameError || nextBusinessError || nextEmailError || phoneValidationError) {
          setError(nextNameError || nextBusinessError || nextEmailError || phoneValidationError);
          setLoading(false);
          return;
        }

        if (formData.password.length < 6) {
          setError(language === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        const response = await api.post('/auth/register', {
          name: formData.name,
          business_name: formData.business_name,
          email: formData.email,
          phone_number: fullPhoneNumber,
          password: formData.password,
          language,
        });

        if (response.data.requiresEmailVerification) {
          setEmailVerificationPending({
            emailMasked: response.data.email_masked,
          });
          setIsLogin(true);
          setFormData({ name: '', business_name: '', email: '', password: '' });
          setPhoneValue('966');
          setPhoneDialCode('966');
          setNameError('');
          setBusinessError('');
          setEmailError('');
          setPhoneError('');
        } else if (response.data.token) {
          // Old behavior for approved users (shouldn't happen for new registrations)
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          onLogin(response.data.user);
        }
      }
    } catch (err) {
      let errorMessage = '';
      
      if (err.response?.data?.error) {
        const serverError = err.response.data.error;
        
        // Improve error messages for better user experience
        if (isLogin) {
          // Login errors - use server message directly or translate
          if (serverError.includes('not found') || serverError.includes('register first')) {
            errorMessage = language === 'ar' 
              ? 'رقم الهاتف غير مسجل. يرجى إنشاء حساب جديد أولاً'
              : serverError;
          } else if (serverError.includes('Incorrect password') || serverError.includes('password')) {
            errorMessage = language === 'ar'
              ? 'كلمة المرور غير صحيحة. يرجى المحاولة مرة أخرى'
              : serverError;
          } else {
            const d = err.response?.data || {};
            errorMessage =
              language === 'ar'
                ? d.error_ar || serverError
                : d.error || d.error_ar || serverError;
          }
        } else {
          // Registration errors
          if (err.response?.status === 403) {
            const d = err.response.data || {};
            errorMessage =
              language === 'ar'
                ? d.error_ar || d.error || 'التسجيل غير متاح حالياً'
                : d.error || d.error_ar || 'Registration is temporarily closed';
          } else if (serverError.includes('already registered') || serverError.includes('already exists')) {
            errorMessage = language === 'ar'
              ? 'رقم الهاتف مسجل بالفعل. يرجى تسجيل الدخول'
              : 'Phone number already registered. Please login instead.';
          } else {
            errorMessage =
              language === 'ar'
                ? err.response.data.error_ar || serverError
                : serverError || err.response.data.error_ar;
          }
        }
      } else {
        // Network or other errors
        errorMessage = language === 'ar' 
          ? 'حدث خطأ. يرجى التحقق من الاتصال والمحاولة مرة أخرى'
          : 'An error occurred. Please check your connection and try again';
      }
      
      setError(errorMessage);
      setErrorTimestamp(Date.now()); // Record when error was set
      setLoading(false); // Make sure loading is set to false
      
      // Error will persist for at least 5 seconds before it can be cleared by typing
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="auth-language-selector">
        <select
          value={language}
          onChange={handleLanguageChange}
          className="language-select"
        >
          <option value="ar">{language === 'ar' ? 'العربية' : 'Arabic'}</option>
          <option value="en">{language === 'ar' ? 'الإنجليزية' : 'English'}</option>
        </select>
      </div>
      <div
        className={`auth-card${emailVerificationPending ? ' auth-card--success-state' : ''}${
          !isLogin &&
          !emailVerificationPending &&
          (!publicRegSettings || publicRegSettings.public_signup_enabled !== false)
            ? ' auth-card--register'
            : ''
        }`}
      >
        <div className="auth-header">
          <div className="auth-logo-container">
            <img src={realLogo} alt="Real" className="auth-logo" />
          </div>
          <p className="auth-slogan">
            {language === 'ar' ? 'حلول عقارية، نتائج ملموسة' : 'Real Estate Real Results'}
          </p>
          {(emailVerificationPending ||
            (!isLogin && publicRegSettings && publicRegSettings.public_signup_enabled === false)) && (
            <p className="auth-subtitle">
              {emailVerificationPending
                ? language === 'ar'
                  ? 'تأكيد البريد الإلكتروني'
                  : 'Verify your email'
                : language === 'ar'
                  ? 'التسجيل متوقف مؤقتاً'
                  : 'Sign-up paused'}
            </p>
          )}
        </div>

        {emailVerificationPending ? (
          <div className="auth-registration-success" role="status" aria-live="polite">
            <p className="auth-step-badge">
              {language === 'ar' ? 'الخطوة ١ من ٢' : 'Step 1 of 2'}
            </p>
            <div className="auth-success-visual" aria-hidden>
              <span className="auth-success-icon">✉</span>
            </div>
            <h2 className="auth-success-title">
              {language === 'ar' ? 'تحقق من بريدك الإلكتروني' : 'Verify your email'}
            </h2>
            {emailVerificationPending.emailMasked && (
              <p className="auth-success-personal" dir="ltr">
                {emailVerificationPending.emailMasked}
              </p>
            )}
            <p className="auth-success-lead auth-success-lead--compact">
              {language === 'ar'
                ? 'أرسلنا رابط تأكيد. افتحه خلال 24 ساعة لإكمال التسجيل.'
                : 'We sent a verification link. Open it within 24 hours to complete sign-up.'}
            </p>
            <p className="auth-success-note">
              {language === 'ar'
                ? 'لم تجد الرسالة؟ تحقق من «غير الهام» أو «الرسائل المزعجة».'
                : "Didn't get it? Check spam or junk folder."}
            </p>
            <button
              type="button"
              className="auth-button auth-button-success-cta"
              onClick={() => {
                setEmailVerificationPending(null);
              }}
            >
              {language === 'ar' ? 'حسناً' : 'OK'}
            </button>
          </div>
        ) : !isLogin && publicRegSettings && publicRegSettings.public_signup_enabled === false ? (
          <div className="auth-registration-closed" role="status" aria-live="polite">
            <p className="auth-success-lead">
              {language === 'ar'
                ? (publicRegSettings.signup_closed_message_ar || 'التسجيل المباشر غير متاح حالياً. تابع إعلانات Real أو تواصل مع فريق المبيعات.')
                : (publicRegSettings.signup_closed_message_en || 'Self-service sign-up is not available right now. Follow Real for updates or contact our team.')}
            </p>
            {(publicRegSettings.rollout_phase || publicRegSettings.target_go_live_at) && (
              <div className="auth-closed-meta">
                {publicRegSettings.rollout_phase && (
                  <p className="auth-success-personal">{publicRegSettings.rollout_phase}</p>
                )}
                {publicRegSettings.target_go_live_at && (
                  <p className="auth-success-brand">
                    {language === 'ar' ? 'إطلاق مستهدف: ' : 'Target: '}
                    {String(publicRegSettings.target_go_live_at).slice(0, 10)}
                  </p>
                )}
              </div>
            )}
            <button type="button" className="auth-button auth-button-success-cta" onClick={() => setIsLogin(true)}>
              {language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to sign in'}
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="auth-form">
          <p className="auth-subtitle auth-subtitle--form">
            {isLogin
              ? language === 'ar'
                ? 'تسجيل الدخول'
                : 'Login'
              : language === 'ar'
                ? 'إنشاء حساب جديد'
                : 'Create Account'}
          </p>
          {!isLogin && (
            <>
              <div className="form-group">
                <label>{language === 'ar' ? 'الاسم' : 'Name'}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                  required={!isLogin}
                  minLength={2}
                />
                {nameError && <small className="field-error">{nameError}</small>}
              </div>
              <div className="form-group">
                <label>{language === 'ar' ? 'اسم المنشأة' : 'Business Name'}</label>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  placeholder={language === 'ar' ? 'أدخل اسم منشأتك' : 'Enter your business name'}
                  required={!isLogin}
                  minLength={3}
                />
                {businessError && <small className="field-error">{businessError}</small>}
              </div>
              <div className="form-group">
                <label>{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email address'}
                  required={!isLogin}
                  autoComplete="email"
                />
                {emailError && <small className="field-error">{emailError}</small>}
                {!emailError && (
                  <small className="form-hint">
                    {language === 'ar'
                      ? 'مطلوب — سنرسل رابط تأكيد ونتواصل معك عند الموافقة'
                      : 'Required — we’ll send a verification link and contact you after approval'}
                  </small>
                )}
              </div>
            </>
          )}

          <div className="auth-credential-group">
            <div className="form-group">
              <label>{language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}</label>
              <PhoneInput
                country="sa"
                value={phoneValue}
                onChange={handlePhoneChange}
                countryCodeEditable={false}
                preferredCountries={['sa', 'ae', 'kw', 'qa', 'bh', 'om']}
                containerClass={`rpi-container${phoneError ? ' rpi-error' : ''}`}
                inputProps={{ required: true, name: 'phone_number', autoComplete: 'tel' }}
              />
              {phoneError && (
                <small className="field-error">{phoneError}</small>
              )}
              {!phoneError && phoneValue.length > phoneDialCode.length && (
                <small className="field-hint">
                  {language === 'ar' ? '✓ رقم صحيح' : '✓ Valid format'}
                </small>
              )}
            </div>

            <div className="form-group">
              <label>{language === 'ar' ? 'كلمة المرور' : 'Password'}</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={language === 'ar' ? 'أدخل كلمة المرور' : 'Enter password'}
                required
                minLength={6}
              />
              {!isLogin && (
                <small className="form-hint">
                  {language === 'ar' ? '6 أحرف على الأقل' : 'At least 6 characters'}
                </small>
              )}
            </div>
          </div>

          {error && (
            <div className="error-message" style={{ animation: 'fadeIn 0.3s ease-in' }}>
              <strong style={{ display: 'block', marginBottom: '6px' }}>
                {language === 'ar' ? '⚠️ خطأ:' : '⚠️ Error:'}
              </strong>
              <div>{error}</div>
              {isLogin && error.includes('not found') && (
                <div style={{ marginTop: '8px', fontSize: '0.9rem', opacity: 0.9 }}>
                  {language === 'ar' 
                    ? '💡 ليس لديك حساب؟ اضغط على "سجل الآن" أدناه'
                    : '💡 Don\'t have an account? Click "Sign up" below'}
                </div>
              )}
            </div>
          )}

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading 
              ? (language === 'ar' ? 'جاري المعالجة...' : 'Processing...')
              : (isLogin 
                  ? (language === 'ar' ? 'تسجيل الدخول' : 'Login')
                  : (language === 'ar' ? 'إنشاء حساب' : 'Create Account')
                )
            }
          </button>
        </form>
        )}

        {!emailVerificationPending &&
          (!publicRegSettings || isLogin || publicRegSettings.public_signup_enabled !== false) && (
        <div className="auth-footer">
          <button
            type="button"
            className="auth-toggle"
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setErrorTimestamp(null);
              setPhoneError('');
              setNameError('');
              setBusinessError('');
              setEmailError('');
              setFormData({ name: '', business_name: '', email: '', password: '' });
              setPhoneValue('966');
              setPhoneDialCode('966');
            }}
          >
            {isLogin 
              ? (language === 'ar' ? 'ليس لديك حساب؟ سجل الآن' : "Don't have an account? Sign up")
              : (language === 'ar' ? 'لديك حساب بالفعل؟ سجل الدخول' : 'Already have an account? Login')
            }
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default Auth;

